import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CompanyPartner } from '@interfaces/common/company';
import { dedupePartnersByName } from '@src/app/features/dashboard/economic-groups/components/modals/quick-create-company-modal/dedupe-partners.util';
import { Partner } from '@interfaces/cnpj/partner';
import { CompanyRequest } from '@models/company/company-request';
import { EconomicGroupsHttpService } from '@services/http/economic-groups/economic-groups-http.service';
import { CompanyService } from '@services/http/company.service';
import { OnboardingService } from '@services/http/onboarding.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { FormErrorHandlerService } from '@services/helpers/form-error-handler.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { CompanyPartnerModalComponent } from '@src/app/features/dashboard/company/company-register/company-partner-modal/company-partner-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, filter, finalize, switchMap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { MenuItem } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { TableModule } from 'primeng/table';

@Component({
  selector: 'app-quick-create-company-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedModule,
    CompanyPartnerModalComponent,
    TableModule,
    MenuModule,
  ],
  templateUrl: './quick-create-company-modal.component.html',
  styleUrls: ['./quick-create-company-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuickCreateCompanyModalComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef!: DialogComponent;
  @ViewChild('partnerModal') partnerModalRef!: CompanyPartnerModalComponent;
  @Output() companyCreated = new EventEmitter<void>();

  form!: FormGroup;
  loadingCnpj = false;
  partners: CompanyPartner[] = [];
  selectedPartnerIndex = -1;

  private groupId!: string;
  private cnpjSearch$ = new Subject<string>();

  constructor(
    translateService: TranslateService,
    private readonly fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef,
    private readonly groupsService: EconomicGroupsHttpService,
    private readonly companyService: CompanyService,
    private readonly onboardingService: OnboardingService,
    private readonly toast: ToastHelperService,
    private readonly formErrorHandler: FormErrorHandlerService
  ) {
    super(translateService);
    this.buildForm();
    this.setupCnpjAutoFill();
  }

  open(groupId: string): void {
    this.groupId = groupId;
    this.resetState();
    this.dialogRef.open('', '', '', 'Cancelar', '', 'quick-create-company-modal', false, false);
  }

  isFormValid(): boolean {
    return this.form.valid;
  }

  onCnpjChange(cnpj: string): void {
    const digits = cnpj?.replace(/\D/g, '');
    if (digits?.length === 14) this.cnpjSearch$.next(digits);
  }

  resetState(): void {
    this.form.reset();
    this.partners = [];
    this.selectedPartnerIndex = -1;
  }

  openAddPartner(): void {
    this.selectedPartnerIndex = -1;
    this.partnerModalRef.open(null, '');
  }

  openEditPartner(partner: CompanyPartner, index: number): void {
    this.selectedPartnerIndex = index;
    this.partnerModalRef.open({ ...partner }, '');
  }

  onPartnerConfirmed(partner: CompanyPartner): void {
    const updated = [...this.partners];
    if (this.selectedPartnerIndex >= 0) {
      updated[this.selectedPartnerIndex] = partner;
    } else {
      updated.push(partner);
    }
    this.partners = updated;
    this.cdr.markForCheck();
  }

  removePartner(index: number): void {
    this.partners = this.partners.filter((_, i) => i !== index);
    this.cdr.markForCheck();
  }

  getPartnerMenuActions(partner: CompanyPartner, index: number): MenuItem[] {
    return [
      {
        label: 'Editar',
        icon: 'pi pi-pencil',
        command: () => this.openEditPartner(partner, index),
      },
      {
        label: 'Remover',
        icon: 'pi pi-trash',
        command: () => this.removePartner(index),
      },
    ];
  }

  submitAction(): void {
    if (!this.isFormValid()) {
      this.form.markAllAsTouched();
      this.toast.showToastError('Preencha os campos obrigatórios.');
      return;
    }

    this.isLoading = true;
    const { cnpj, corporateName, tradeName } = this.form.getRawValue();

    const companyPayload: CompanyRequest = {
      cnpj,
      corporateName,
      tradeName: tradeName ?? '',
      status: '1',
      address: '',
      zipCode: '',
      city: '',
      state: '',
      sector: '',
      website: '',
      surname: '',
      partners: this.partners,
      userEmails: [],
      sendOnboardingEmail: false,
    };

    this.companyService
      .postCompany(companyPayload)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: res => {
          // O `VaultApiService` já devolve o `data` do envelope: aqui chega o `CreateCompanyDto` com o `id` da empresa
          // criada (o backend passou a preenchê-lo no card 3578). Ler `res.data.id` dava sempre vazio e a empresa
          // nunca entrava no grupo.
          const companyId = (res as unknown as { id?: string | null } | null)?.id;
          if (companyId) {
            this.groupsService
              .addMember(this.groupId, { companyId, orderIndex: 0 })
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: () => {
                  this.toast.showToastSuccess('Empresa criada e adicionada ao grupo.');
                  this.companyCreated.emit();
                  this.dialogRef.close();
                },
                error: () => {
                  this.toast.showToastSuccess('Empresa criada. Adicione-a ao grupo manualmente.');
                  this.companyCreated.emit();
                  this.dialogRef.close();
                },
              });
          } else {
            this.toast.showToastSuccess('Empresa criada com sucesso.');
            this.companyCreated.emit();
            this.dialogRef.close();
          }
        },
        error: err => this.formErrorHandler.showToast(err, 'Erro ao criar empresa.'),
      });
  }

  private buildForm(): void {
    this.form = this.fb.group({
      cnpj: [null, [Validators.required, Validators.minLength(14)]],
      corporateName: [null, Validators.required],
      tradeName: [null],
    });
  }

  private mapPartners(rawPartners: Partner[]): CompanyPartner[] {
    // A Receita repete a pessoa uma vez por cargo: um sócio por pessoa, com os cargos juntos (card 3578).
    return dedupePartnersByName(this.mapRawPartners(rawPartners));
  }

  private mapRawPartners(rawPartners: Partner[]): CompanyPartner[] {
    return rawPartners
      .filter(p => !!p.name)
      .map(p => ({
        id: null,
        companyId: '',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        company: null as any,
        name: p.name,
        qualification: p.qualification ?? '',
        countryOfOrigin: p.countryOfOrigin ?? '',
        legalRepresentativeName: p.legalRepresentativeName ?? '',
        legalRepresentativeQualification: p.legalRepresentativeQualification ?? '',
      }));
  }

  private setupCnpjAutoFill(): void {
    this.cnpjSearch$
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        filter(cnpj => !!cnpj),
        switchMap(cnpj => {
          this.loadingCnpj = true;
          this.cdr.markForCheck();
          return this.onboardingService.getCompanyDataFromCnpj(cnpj);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: data => {
          this.loadingCnpj = false;
          if (data) {
            this.form.patchValue({
              corporateName: data.name,
              tradeName: data.tradeName,
            });
            this.partners = this.mapPartners(data.detailedPartners ?? []);
          }
          // Two-phase refresh: first pass propagates valueChanges to app-input's
          // internalValue; second pass re-renders the [ngModel] binding in the child
          this.cdr.detectChanges();
          setTimeout(() => this.cdr.detectChanges(), 0);
        },
        error: () => {
          this.loadingCnpj = false;
          this.cdr.markForCheck();
        },
      });
  }
}
